import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { LuSearch, LuSend, LuUsers, LuFileText, LuMegaphone, LuChevronDown, LuCheck, LuRefreshCw } from 'react-icons/lu';
import Loading from '../../../shared/components/Loading/Loading';
import { fetchAllCustomers, fetchTemplates, sendWhatsAppCampaign, fetchCampaigns, retryCampaign } from './campaignService';
import './CampanhasPage.css';

export default function CampanhasPage() {
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [retrying, setRetrying] = useState(null); // id da campanha em retry
  const [campaignsLoading, setCampaignsLoading] = useState(true);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadCampaigns();
    loadTemplates();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      q
        ? customers.filter(
            (c) => c.name?.toLowerCase().includes(q) || c.phone?.includes(q)
          )
        : customers
    );
  }, [search, customers]);

  async function loadCustomers() {
    setLoading(true);
    try {
      const data = await fetchAllCustomers();
      setCustomers(data);
      setFiltered(data);
    } catch {
      toast.error('Erro ao carregar contatos.');
    } finally {
      setLoading(false);
    }
  }

  async function loadTemplates() {
    setTemplatesLoading(true);
    try {
      const data = await fetchTemplates();
      setTemplates(data);
      if (data.length === 1) setSelectedTemplate(data[0]);
    } catch {
      toast.error('Erro ao carregar templates do Twilio.');
    } finally {
      setTemplatesLoading(false);
    }
  }

  async function loadCampaigns() {
    setCampaignsLoading(true);
    try {
      const data = await fetchCampaigns();
      setCampaigns(data);
    } catch {
      // histórico é opcional
    } finally {
      setCampaignsLoading(false);
    }
  }

  function toggleAll() {
    if (selected.size === filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((c) => c.phone)));
    }
  }

  function toggleOne(phone) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  }

  function selectTemplate(tpl) {
    setSelectedTemplate(tpl);
    setTemplateOpen(false);
  }

  async function handleSend() {
    if (!selectedTemplate) {
      toast.warning('Selecione um template antes de enviar.');
      return;
    }
    if (selectedTemplate.approvalStatus === 'rejected') {
      toast.error('Template rejeitado pela Meta. Corrija o conteúdo e submeta novamente no Twilio.');
      return;
    }
    if (selected.size === 0) {
      toast.warning('Selecione pelo menos um contato.');
      return;
    }

    const contacts = customers
      .filter((c) => selected.has(c.phone))
      .map((c) => ({ name: c.name, phone: c.phone }));

    setSending(true);
    try {
      const result = await sendWhatsAppCampaign({
        contacts,
        message: selectedTemplate.name,
        contentSid: selectedTemplate.sid,
        templateVariables: selectedTemplate.variables || {},
      });
      if (result.sent > 0) {
        toast.success(`Campanha enviada: ${result.sent} entregue(s), ${result.failed} falha(s).`);
      } else {
        const detail = result.firstError ? ` Erro: ${result.firstError}` : '';
        toast.error(`Nenhuma mensagem foi enviada.${detail}`);
      }
      setSelected(new Set());
      loadCampaigns();
    } catch (err) {
      toast.error(`Erro ao enviar campanha: ${err?.response?.data?.message || err?.message || 'tente novamente.'}`);
    } finally {
      setSending(false);
    }
  }

  async function handleRetry(camp, pendingCount) {
    const contentSid = camp.contentSid || selectedTemplate?.sid;
    if (!contentSid) {
      toast.warning('Selecione um template acima antes de fazer o retry desta campanha antiga.');
      return;
    }
    if (!window.confirm(`Reenviar para ${pendingCount} contato(s) que não receberam a mensagem?`)) return;
    setRetrying(camp._id);
    try {
      const result = await retryCampaign(camp._id, contentSid);
      toast.success(`Retry: ${result.sent} reenviado(s), ${result.failed} falha(s).`);
      loadCampaigns();
    } catch (err) {
      toast.error(`Erro no retry: ${err?.response?.data?.message || err?.message || 'tente novamente.'}`);
    } finally {
      setRetrying(null);
    }
  }

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const canSend = !sending && selected.size > 0 && !!selectedTemplate;

  return (
    <div className="camp-page">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="camp-header">
        <div className="camp-header-icon">
          <LuMegaphone size={20} />
        </div>
        <div>
          <h1 className="camp-title">Campanhas WhatsApp</h1>
          <p className="camp-subtitle">Selecione o template, escolha os contatos e envie</p>
        </div>
      </div>

      <div className="camp-body">
        {/* PAINEL ESQUERDO — Contatos */}
        <div className="camp-panel">
          <div className="camp-panel-header">
            <span className="camp-panel-title">
              <LuUsers size={15} />
              Contatos
            </span>
            {!loading && (
              <span className="camp-count">{selected.size}/{filtered.length} selecionados</span>
            )}
          </div>

          <div className="camp-search-wrap">
            <div className="camp-search-inner">
              <LuSearch size={15} className="camp-search-icon" />
              <input
                type="text"
                placeholder="Buscar por nome ou telefone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="camp-search-input"
              />
            </div>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <>
              <button className="camp-select-all" onClick={toggleAll}>
                <span className={`camp-cb ${allSelected ? 'camp-cb--on' : ''}`} />
                Selecionar todos ({filtered.length})
              </button>

              <div className="camp-contact-list">
                {filtered.length === 0 ? (
                  <div className="camp-empty">Nenhum contato encontrado.</div>
                ) : (
                  filtered.map((c) => {
                    const checked = selected.has(c.phone);
                    return (
                      <div
                        key={c.phone}
                        className={`camp-contact ${checked ? 'camp-contact--on' : ''}`}
                        onClick={() => toggleOne(c.phone)}
                      >
                        <span className={`camp-cb ${checked ? 'camp-cb--on' : ''}`} />
                        <div className="camp-contact-info">
                          <span className="camp-contact-name">{c.name}</span>
                          <span className="camp-contact-phone">{c.phone}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* PAINEL DIREITO — Template + Envio */}
        <div className="camp-panel camp-panel--msg">
          <div className="camp-panel-header">
            <span className="camp-panel-title">
              <LuFileText size={15} />
              Template
            </span>
          </div>

          {/* SELETOR DE TEMPLATE */}
          <div className="camp-tpl-wrap">
            {templatesLoading ? (
              <Loading size="sm" />
            ) : templates.length === 0 ? (
              <div className="camp-tpl-empty">
                Nenhum template encontrado no Twilio.
              </div>
            ) : (
              <div className="camp-tpl-dropdown">
                <button
                  className="camp-tpl-trigger"
                  onClick={() => setTemplateOpen((o) => !o)}
                >
                  <span className="camp-tpl-trigger-label">
                    {selectedTemplate ? selectedTemplate.name : 'Selecione um template...'}
                  </span>
                  <LuChevronDown size={14} className={`camp-tpl-chevron ${templateOpen ? 'camp-tpl-chevron--open' : ''}`} />
                </button>

                {templateOpen && (
                  <div className="camp-tpl-list">
                    {templates.map((tpl) => (
                      <button
                        key={tpl.sid}
                        className={`camp-tpl-option ${selectedTemplate?.sid === tpl.sid ? 'camp-tpl-option--on' : ''}`}
                        onClick={() => selectTemplate(tpl)}
                      >
                        <div className="camp-tpl-option-info">
                          <span className="camp-tpl-option-name">{tpl.name}</span>
                          <span className={`camp-tpl-approval camp-tpl-approval--${tpl.approvalStatus || 'unknown'}`}>
                            {tpl.approvalStatus === 'approved' ? '✓ Aprovado' :
                             tpl.approvalStatus === 'pending' ? '⏳ Aguardando Meta' :
                             tpl.approvalStatus === 'rejected' ? '✗ Rejeitado' : tpl.approvalStatus || '?'}
                          </span>
                        </div>
                        {selectedTemplate?.sid === tpl.sid && <LuCheck size={14} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* PREVIEW DO TEMPLATE */}
          {selectedTemplate?.body && (
            <div className="camp-tpl-preview">
              <span className="camp-tpl-preview-label">Prévia da mensagem</span>
              <div className="camp-tpl-preview-bubble">
                {selectedTemplate.body.replace('{{1}}', 'Nome do cliente')}
              </div>
            </div>
          )}

          <div className="camp-actions">
            <div className="camp-send-meta">
              <span>{selected.size} destinatário(s) selecionado(s)</span>
            </div>
            <button
              className="camp-send-btn"
              onClick={handleSend}
              disabled={!canSend}
            >
              {sending ? (
                <Loading size="sm" />
              ) : (
                <>
                  <LuSend size={14} />
                  Enviar via WhatsApp
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* HISTÓRICO DE CAMPANHAS */}
      <div className="camp-history">
        <h2 className="camp-history-title">Campanhas Enviadas</h2>

        {campaignsLoading ? (
          <Loading />
        ) : campaigns.length === 0 ? (
          <div className="camp-empty camp-empty--center">
            Nenhuma campanha enviada ainda. Envie sua primeira campanha acima!
          </div>
        ) : (
          <div className="camp-history-list">
            {campaigns.map((camp) => {
              const allFailed = camp.totalFailed > 0 && camp.totalFailed === camp.totalSent;
              const partialFail = camp.totalFailed > 0 && !allFailed;
              const pendingRetry = camp.pendingRetry ?? 0;
              const canRetry = pendingRetry > 0;
              const isRetrying = retrying === camp._id;
              return (
              <div
                key={camp._id}
                className={`camp-history-item${allFailed ? ' camp-history-item--fail' : partialFail ? ' camp-history-item--partial' : ''}`}
              >
                <div className="camp-history-meta">
                  <span className="camp-history-date">{formatDate(camp.sentAt)}</span>
                  {allFailed && <span className="camp-history-badge camp-history-badge--fail">Falhou</span>}
                  {partialFail && <span className="camp-history-badge camp-history-badge--partial">Parcial</span>}
                  <span className="camp-history-preview">
                    {camp.message?.slice(0, 80)}{camp.message?.length > 80 ? '...' : ''}
                  </span>
                </div>
                <div className="camp-stats">
                  <div className="camp-stat">
                    <span className="camp-stat-val">{camp.totalSent ?? 0}</span>
                    <span className="camp-stat-lbl">Enviados</span>
                  </div>
                  {camp.totalFailed > 0 && (
                    <div className="camp-stat camp-stat--fail">
                      <span className="camp-stat-val">{camp.totalFailed}</span>
                      <span className="camp-stat-lbl">Falhas</span>
                    </div>
                  )}
                  <div className="camp-stat">
                    <span className="camp-stat-val">{camp.delivered ?? '—'}</span>
                    <span className="camp-stat-lbl">Entregues</span>
                  </div>
                  <div className="camp-stat">
                    <span className="camp-stat-val">{camp.read ?? '—'}</span>
                    <span className="camp-stat-lbl">Lidos</span>
                  </div>
                  <div className="camp-stat">
                    <span className="camp-stat-val">{camp.replied ?? '—'}</span>
                    <span className="camp-stat-lbl">Respondidos</span>
                  </div>
                  {canRetry && (
                    <button
                      className="camp-retry-btn"
                      onClick={() => handleRetry(camp, pendingRetry)}
                      disabled={isRetrying}
                      title={`Reenviar para ${pendingRetry} contatos pendentes`}
                    >
                      {isRetrying ? <Loading size="sm" /> : <LuRefreshCw size={13} />}
                      {isRetrying ? 'Reenviando…' : `Retry (${pendingRetry})`}
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
